import React, { memo, useCallback, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/shadcn/ui/dialog';
import { Button } from '@/shared/shadcn/ui/Button';
import { usePWAUpdate } from '@/shared/hooks/usePWAUpdate';

/**
 * PWA Update Dialog Component
 * 
 * Displays a dialog when a new service worker version is available.
 * The dialog prompts the user to reload the page to get the latest version.
 * 
 * Features:
 * - Only renders in production builds (SW not available in dev)
 * - Prevents dialog flicker with controlled open state
 * - Single instance - should be rendered once at app root
 */

interface PWAUpdateDialogProps {
  /** Enable debug logging */
  debug?: boolean;
}

export const PWAUpdateDialog: React.FC<PWAUpdateDialogProps> = memo(({ debug }) => {
  const { needRefresh, update } = usePWAUpdate({ debug });
  const [isUpdating, setIsUpdating] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when needRefresh changes to true (new update available)
  useEffect(() => {
    if (needRefresh) {
      setDismissed(false);
    }
  }, [needRefresh]);

  const handleUpdate = useCallback(() => {
    try {
      setIsUpdating(true);
      update();
    } catch (e) {
      setIsUpdating(false);
    }
  }, [update]);

  // Don't render anything if no update is needed or user dismissed
  if (!needRefresh || dismissed) {
    console.log('[PWA] PWAUpdateDialog: needRefresh', needRefresh);
    console.log('[PWA] PWAUpdateDialog: dismissed', dismissed);
    return null;
  }

  const isOpen = needRefresh && !dismissed;
  console.log('[PWA] PWAUpdateDialog: isOpen', isOpen);
  return (
    <Dialog open={isOpen} onOpenChange={(value) => !value && setDismissed(true)}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5 text-blue-600"
            >
              <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
              <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
              <path d="M16 16h5v5" />
            </svg>
            Update Available
          </DialogTitle>
          <DialogDescription className="pt-2">
            A new version is available. Please reload the page to continue.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => setDismissed(true)}>
            Later
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
          >
            {isUpdating ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Updating...
              </>
            ) : (
              'Reload Now'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

PWAUpdateDialog.displayName = 'PWAUpdateDialog';

export default PWAUpdateDialog;
